import { baseService } from "src/store/base.store";
import { executeQuery } from "src/store";
import { userReportRepository } from "../repositories/user-report.repository";
import { UserReport } from "../types/user-report.entity";
import { FilterQuery } from "src/firebase/firestore/types";

export interface UserReportsFilter {
  reporterId?: string;
  reportedId?: string;
  reportedFrom?: string;
  reason?: string;
}

const buildWhere = (where?: UserReportsFilter): FilterQuery<UserReport>["where"] => {
  if (!where) return undefined;
  const q: Record<string, unknown> = {};
  if (where.reporterId != null) q.reporterId = where.reporterId;
  if (where.reportedId != null) q.reportedId = where.reportedId;
  if (where.reportedFrom != null) q.reportedFrom = where.reportedFrom;
  if (where.reason != null) q.reason = where.reason;
  return Object.keys(q).length ? (q as FilterQuery<UserReport>["where"]) : undefined;
};

export const userReportService = baseService.injectEndpoints({
  endpoints: (builder) => ({
    getUsersReports: builder.query<
      (UserReport & { id: string })[],
      { where?: UserReportsFilter; offset?: string; limit?: number }
    >({
      queryFn: async ({ where, offset, limit }) => {
        const filterWhere = buildWhere(where);
        const pageSize = typeof limit === "number" && limit > 0 ? limit : 20;
        const queryOptions: FilterQuery<UserReport> = {
          where: filterWhere,
          limit: pageSize,
          orderBy: ["createdAt", "desc"],
        };
        if (offset) queryOptions.offset = offset;
        const res = await executeQuery(() =>
          userReportRepository.find(queryOptions)
        );
        if ("error" in res) return { error: res.error } as any;
        return { data: res.data as (UserReport & { id: string })[] };
      },
      providesTags: ["users-reports"],
    }),

    countUsersReports: builder.query<number, { where?: UserReportsFilter }>({
      queryFn: async ({ where }) => {
        const filterWhere = buildWhere(where);
        const res = await executeQuery(() =>
          userReportRepository.count(filterWhere)
        );
        if ("error" in res) return { error: res.error } as any;
        return { data: res.data as number };
      },
      providesTags: ["users-reports"],
    }),

    getUsersReportById: builder.query<UserReport & { id: string }, string>({
      queryFn: async (id) => {
        const res = await executeQuery(() =>
          userReportRepository.findById(id)
        );
        if ("error" in res) return { error: res.error } as any;
        return { data: res.data as UserReport & { id: string } };
      },
      providesTags: (_result, _error, id) => [{ type: "users-reports", id }],
    }),

    setUsersReportHandledState: builder.mutation<void, { id: string; isHandled: boolean }>({
      queryFn: async ({ id, isHandled }) => {
        const res = await executeQuery(() =>
          userReportRepository.update(id, { isHandled })
        );
        if ("error" in res) return { error: res.error } as any;
        return { data: undefined };
      },
      invalidatesTags: (_result, _error, payload) => [
        "users-reports",
        { type: "users-reports", id: payload.id },
      ],
    }),
  }),
});

export const {
  useGetUsersReportsQuery,
  useCountUsersReportsQuery,
  useGetUsersReportByIdQuery,
  useSetUsersReportHandledStateMutation,
} = userReportService;
